import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import { Check, Calendar, ChevronLeft, ChevronRight, Maximize, Filter, ArrowUp, ArrowDown, Layers, Minus, Plus, Rows3 } from "lucide-react";
import clsx from "clsx";
import { useAgendaStore, SortField, GroupMode } from "./agenda.store";
import { AgendaGroup, AgendaJobData } from "./agenda.types";
import { GanttChart } from "./components/gantt-chart.component";
import { PageTitle } from "@/components/src/page-title/page-title.component";
import { JobStatusEnum, jobStatusToLabel } from "@/lib/types/job";
import { getJobColorClass, PENDENT_UNTOUCHED_COLOR } from "./components/utils";

export interface SharedAgendaPageProps {
	organizationId: string;
	profileId: string;
	appId?: string;
	fetchGroups: () => Promise<AgendaGroup[]>;
	onJobClick: (job: AgendaJobData, groupId: string) => void;
	onJobNavigate: (groupId: string, jobId: number) => void;
	renderHeader?: (title: React.ReactNode) => React.ReactNode;
	fullScreenHandle?: { active: boolean; enter: () => void };
	profileColors?: Record<string, string>;
	profilesMap?: Record<string, { first_name?: string; last_name?: string }>;
	groupLabel?: string;
}

const SORT_FIELD_LABELS: Record<string, Record<SortField, string>> = {
	default: {
		outgoing: "Lieferdatum",
		incoming: "Eingangsdatum",
		code: "Auftragscode",
		client: "Kunde",
	},
	dentconnect: {
		outgoing: "Lieferdatum",
		incoming: "Eingang im Labor",
		code: "Auftragscode",
		client: "Labor",
	},
};

const FILTER_STATUSES = [
	// JobStatusEnum.PRE_REGISTERED,
	JobStatusEnum.IN_PROGRESS,
	JobStatusEnum.COMPLETED,
	JobStatusEnum.ARCHIVED,
];

export const SharedAgendaPage: React.FC<SharedAgendaPageProps> = ({
	profileId,
	appId,
	fetchGroups,
	onJobClick,
	onJobNavigate,
	renderHeader,
	fullScreenHandle,
	profileColors,
	profilesMap,
	groupLabel = "Kunden",
}) => {
	const sortLabels = SORT_FIELD_LABELS[appId ?? "default"] ?? SORT_FIELD_LABELS.default;
	const store = useAgendaStore((s) => ({
		groups: s.groups,
		lastPolledAt: s.lastPolledAt,
		expandedGroupIds: s.expandedGroupIds,
		filteredGroupIds: s.filteredGroupIds,
		dateRange: s.dateRange,
		viewMode: s.viewMode,
		hourViewDate: s.hourViewDate,
		dayRange: s.dayRange,
		hourRange: s.hourRange,
		weekRange: s.weekRange,
		groupMode: s.groupMode,
		sortField: s.sortField,
		sortDirection: s.sortDirection,
		statusFilter: s.statusFilter,
		leavingSoonEnabled: s.leavingSoonEnabled,
		leavingSoonDays: s.leavingSoonDays,
		rowHeight: s.rowHeight,
		setRowHeight: s.setRowHeight,
		initAgenda: s.initAgenda,
		fetchAgendaJobs: s.fetchAgendaJobs,
		toggleGroupExpanded: s.toggleGroupExpanded,
		setFilteredGroupIds: s.setFilteredGroupIds,
		shiftDateRange: s.shiftDateRange,
		shiftHourViewDate: s.shiftHourViewDate,
		setViewMode: s.setViewMode,
		resetToToday: s.resetToToday,
		expandAllGroups: s.expandAllGroups,
		collapseAllGroups: s.collapseAllGroups,
		setDayRange: s.setDayRange,
		setHourRange: s.setHourRange,
		setWeekRange: s.setWeekRange,
		setGroupMode: s.setGroupMode,
		setSortField: s.setSortField,
		toggleSortDirection: s.toggleSortDirection,
		setStatusFilter: s.setStatusFilter,
		setLeavingSoonEnabled: s.setLeavingSoonEnabled,
		setLeavingSoonDays: s.setLeavingSoonDays,
	}));

	useEffect(() => {
		if (profileId) {
			store.initAgenda(profileId, fetchGroups, appId);
		}
	}, [profileId, appId]);

	useEffect(() => {
		store.fetchAgendaJobs();
		const interval = setInterval(store.fetchAgendaJobs, 30_000);
		return () => clearInterval(interval);
	}, []);

	const filteredGroups = useMemo(() => {
		const base =
			store.filteredGroupIds.length === 0
				? store.groups
				: store.groups.filter((g) => store.filteredGroupIds.includes(g.id));

		let result = base;

		if (store.viewMode === "hour") {
			result = result
				.map((g) => ({
					...g,
					jobs: g.jobs.filter(
						(j) =>
							(!j.incoming_order_date || j.incoming_order_date <= store.hourViewDate) &&
							(!j.outgoing_order_date || j.outgoing_order_date >= store.hourViewDate)
					),
				}))
				.filter((g) => g.jobs.length > 0);
		} else {
			const rangeStart = store.dateRange.start;
			const rangeEnd = store.dateRange.end;
			result = result
				.map((g) => ({
					...g,
					jobs: g.jobs.filter((j) => {
						if (!j.incoming_order_date && !j.outgoing_order_date) return false;
						const jobStart = j.incoming_order_date ?? j.outgoing_order_date!;
						const jobEnd = j.outgoing_order_date ?? j.incoming_order_date!;
						return jobStart <= rangeEnd && jobEnd >= rangeStart;
					}),
				}))
				.filter((g) => g.jobs.length > 0);
		}

		if (store.statusFilter.length > 0) {
			result = result
				.map((g) => ({
					...g,
					jobs: g.jobs.filter((j) => store.statusFilter.includes(j.status as number)),
				}))
				.filter((g) => g.jobs.length > 0);
		}

		if (store.leavingSoonEnabled) {
			const todayStr = new Date().toISOString().split("T")[0];
			const futureDate = new Date();
			futureDate.setDate(futureDate.getDate() + store.leavingSoonDays);
			const futureStr = futureDate.toISOString().split("T")[0];
			result = result
				.map((g) => ({
					...g,
					jobs: g.jobs.filter(
						(j) =>
							j.outgoing_order_date &&
							j.outgoing_order_date >= todayStr &&
							j.outgoing_order_date <= futureStr
					),
				}))
				.filter((g) => g.jobs.length > 0);
		}

		return result;
	}, [
		store.groups, store.filteredGroupIds, store.viewMode, store.hourViewDate,
		store.statusFilter, store.leavingSoonEnabled, store.leavingSoonDays,
		store.dateRange.start, store.dateRange.end,
	]);

	const sortJob = useCallback(
		(a: AgendaJobData, b: AgendaJobData): number => {
			const dir = store.sortDirection === "asc" ? 1 : -1;
			let aVal: string | null = null;
			let bVal: string | null = null;

			switch (store.sortField) {
				case "outgoing":
					aVal = (a.outgoing_order_date ?? "") + (a.outgoing_order_time ?? "") || null;
					bVal = (b.outgoing_order_date ?? "") + (b.outgoing_order_time ?? "") || null;
					break;
				case "incoming":
					aVal = (a.incoming_order_date ?? "") + (a.incoming_order_time ?? "") || null;
					bVal = (b.incoming_order_date ?? "") + (b.incoming_order_time ?? "") || null;
					break;
				case "code":
					aVal = a.code;
					bVal = b.code;
					break;
				case "client":
					aVal = a.client_name ?? null;
					bVal = b.client_name ?? null;
					break;
			}

			if (aVal === null && bVal === null) return 0;
			if (aVal === null) return 1;
			if (bVal === null) return -1;
			return aVal.localeCompare(bVal, "de") * dir;
		},
		[store.sortField, store.sortDirection]
	);

	const sortedGroups = useMemo(() => {
		if (store.groupMode === "flat") {
			const allJobs = filteredGroups.flatMap((g) =>
				g.jobs.map((j) => ({ ...j, _groupId: g.id, _groupName: g.name }))
			);
			allJobs.sort(sortJob);
			return [{ id: "__flat__", name: "", jobs: allJobs }];
		}
		const sorted = [...filteredGroups].sort((a, b) =>
			a.name.localeCompare(b.name, "de")
		);
		return sorted.map((g) => ({
			...g,
			jobs: [...g.jobs].sort(sortJob),
		}));
	}, [filteredGroups, sortJob, store.groupMode]);

	const visibleGroupIdsRef = useRef<string[]>([]);
	visibleGroupIdsRef.current = sortedGroups.map((g) => g.id);

	const handleExpandAll = useCallback(() => {
		store.expandAllGroups(visibleGroupIdsRef.current);
	}, [store.expandAllGroups]);

	const [scrollTrigger, setScrollTrigger] = useState(0);

	const dateLabel = useMemo(() => {
		if (store.viewMode === "hour") {
			const d = new Date(store.hourViewDate);
			return d.toLocaleDateString("de-DE", {
				weekday: "short",
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
			});
		}
		const startLabel = new Date(store.dateRange.start).toLocaleDateString("de-DE", {
			day: "2-digit",
			month: "2-digit",
		});
		const endLabel = new Date(store.dateRange.end).toLocaleDateString("de-DE", {
			day: "2-digit",
			month: "2-digit",
		});
		return `${startLabel} - ${endLabel}`;
	}, [store.viewMode, store.hourViewDate, store.dateRange.start, store.dateRange.end]);

	const currentRange = store.viewMode === "hour" ? store.hourRange : store.viewMode === "day" ? store.dayRange : store.weekRange;
	const setCurrentRange = store.viewMode === "hour" ? store.setHourRange : store.viewMode === "day" ? store.setDayRange : store.setWeekRange;
	const rangeUnit = store.viewMode === "hour" ? "h" : store.viewMode === "day" ? "d" : "w";

	const activeFilterCount = (store.statusFilter.length > 0 ? 1 : 0) + (store.leavingSoonEnabled ? 1 : 0);

	const header = <PageTitle title={[{ name: "Agenda" }]} icon={<Calendar />} />;

	return (
		<div className="flex flex-col w-full h-full">
			{renderHeader ? renderHeader(header) : <div className="px-4 pt-2">{header}</div>}

			<div className="px-4 pt-2 pb-2 flex flex-col gap-2">
				<div className="flex items-center gap-2 flex-wrap">
					<div className="flex items-center gap-1">
						<button
							className="inline-flex items-center justify-center h-8 w-8 rounded-md text-sm hover:bg-accent hover:text-accent-foreground"
							onClick={() => store.viewMode === "hour" ? store.shiftHourViewDate(-1) : store.shiftDateRange(-1)}
							aria-label="Zurück"
							data-testid="agenda-nav-back"
						>
							<ChevronLeft size={16} />
						</button>
						<button
							className="inline-flex items-center justify-center h-8 rounded-md px-3 text-xs border border-input bg-background hover:bg-accent hover:text-accent-foreground"
							onClick={() => {
								store.resetToToday();
								setScrollTrigger((t) => t + 1);
							}}
							aria-label="Heute anzeigen"
							data-testid="agenda-nav-today"
						>
							Heute
						</button>
						<button
							className="inline-flex items-center justify-center h-8 w-8 rounded-md text-sm hover:bg-accent hover:text-accent-foreground"
							onClick={() => store.viewMode === "hour" ? store.shiftHourViewDate(1) : store.shiftDateRange(1)}
							aria-label="Vorwärts"
							data-testid="agenda-nav-forward"
						>
							<ChevronRight size={16} />
						</button>
					</div>

					<ToggleGroupPrimitive.Root
						type="single"
						value={store.viewMode}
						onValueChange={(v) => {
							if (v) store.setViewMode(v as "day" | "week" | "hour");
						}}
						className="flex items-center justify-center gap-1 h-8"
						data-testid="agenda-view-toggle"
					>
						<ToggleGroupPrimitive.Item
							value="hour"
							className={clsx(
								"inline-flex items-center justify-center rounded-md text-xs h-8 px-3 ring-offset-background transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none",
								store.viewMode === "hour" && "bg-accent text-accent-foreground"
							)}
							aria-label="Stundenansicht"
							data-testid="agenda-view-hour"
						>
							Stunden
						</ToggleGroupPrimitive.Item>
						<ToggleGroupPrimitive.Item
							value="day"
							className={clsx(
								"inline-flex items-center justify-center rounded-md text-xs h-8 px-3 ring-offset-background transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none",
								store.viewMode === "day" && "bg-accent text-accent-foreground"
							)}
							aria-label="Tagesansicht"
							data-testid="agenda-view-day"
						>
							Tag
						</ToggleGroupPrimitive.Item>
						<ToggleGroupPrimitive.Item
							value="week"
							className={clsx(
								"inline-flex items-center justify-center rounded-md text-xs h-8 px-3 ring-offset-background transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none",
								store.viewMode === "week" && "bg-accent text-accent-foreground"
							)}
							aria-label="Wochenansicht"
							data-testid="agenda-view-week"
						>
							Woche
						</ToggleGroupPrimitive.Item>
					</ToggleGroupPrimitive.Root>

					{store.viewMode !== "hour" && (
						<div className="flex items-center gap-0.5 h-8 border border-input rounded-md px-1" data-testid="agenda-range-stepper">
							<button
								className="inline-flex items-center justify-center h-6 w-6 rounded text-xs hover:bg-accent"
								onClick={() => setCurrentRange(Math.max(0, currentRange - 1))}
								aria-label="Bereich verkleinern"
							>
								<Minus size={12} />
							</button>
							<span className="text-xs tabular-nums min-w-[28px] text-center">±{currentRange}{rangeUnit}</span>
							<button
								className="inline-flex items-center justify-center h-6 w-6 rounded text-xs hover:bg-accent"
								onClick={() => setCurrentRange(Math.min(30, currentRange + 1))}
								aria-label="Bereich vergrössern"
							>
								<Plus size={12} />
							</button>
						</div>
					)}

					<RowDensityButton rowHeight={store.rowHeight} onRowHeightChange={store.setRowHeight} />

					<span className="text-xs text-gray-500 ml-1">
						{dateLabel}
					</span>

					<div className="ml-auto flex items-center gap-2">
						<PollingIndicator lastPolledAt={store.lastPolledAt} />
					</div>

					<div className="flex items-center gap-1">
						<SortButton
							sortField={store.sortField}
							sortDirection={store.sortDirection}
							onFieldChange={store.setSortField}
							onToggleDirection={store.toggleSortDirection}
							labels={sortLabels}
						/>
						<AgendaFilterButton
							statusFilter={store.statusFilter}
							onStatusFilterChange={store.setStatusFilter}
							leavingSoonEnabled={store.leavingSoonEnabled}
							leavingSoonDays={store.leavingSoonDays}
							onLeavingSoonEnabledChange={store.setLeavingSoonEnabled}
							onLeavingSoonDaysChange={store.setLeavingSoonDays}
							activeCount={activeFilterCount}
							leavingSoonLabel={appId === "dentconnect" ? "Lieferung in" : "Ausgang in"}
						/>
						<GroupingButton
							groupMode={store.groupMode}
							onGroupModeChange={store.setGroupMode}
							groupLabel={groupLabel}
						/>
						<GroupFilter
							groups={store.groups}
							filteredGroupIds={store.filteredGroupIds}
							onFilterChange={store.setFilteredGroupIds}
							groupLabel={groupLabel}
						/>
						{fullScreenHandle && (
							<button
								className="inline-flex items-center justify-center h-8 rounded-md px-3 text-xs gap-1.5 border border-input bg-background hover:bg-accent hover:text-accent-foreground"
								onClick={fullScreenHandle.enter}
								aria-label="Vollbild"
								data-testid="agenda-fullscreen"
							>
								<Maximize size={14} />
								Vollbild
							</button>
						)}
					</div>
				</div>
			</div>

			<div className="flex-1 px-4 pb-4 min-h-0">
				<div className={clsx("w-full h-full", fullScreenHandle?.active && "fixed inset-0 z-50 bg-white")}>
					<GanttChart
						groups={sortedGroups}
						expandedGroupIds={store.expandedGroupIds}
						onToggleGroup={store.toggleGroupExpanded}
						dateRange={store.dateRange}
						viewMode={store.viewMode}
						hourViewDate={store.hourViewDate}
						fullScreen={fullScreenHandle?.active}
						scrollToTodayTrigger={scrollTrigger}
						onJobNavigate={onJobNavigate}
						onJobClick={onJobClick}
						onExpandAll={handleExpandAll}
						onCollapseAll={store.collapseAllGroups}
						groupMode={store.groupMode}
						rowHeight={store.rowHeight}
						profileColors={profileColors}
						profilesMap={profilesMap}
						groupLabel={groupLabel}
					/>
				</div>
			</div>
		</div>
	);
};

const SortButton: React.FC<{
	sortField: SortField;
	sortDirection: "asc" | "desc";
	onFieldChange: (field: SortField) => void;
	onToggleDirection: () => void;
	labels: Record<SortField, string>;
}> = ({ sortField, sortDirection, onFieldChange, onToggleDirection, labels }) => {
	const [open, setOpen] = useState(false);
	const SortIcon = sortDirection === "asc" ? ArrowUp : ArrowDown;

	return (
		<PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
			<div className="flex items-center h-8 rounded-md border border-input bg-background text-xs">
				<button
					className="inline-flex items-center justify-center h-full w-7 hover:bg-accent rounded-l-md"
					onClick={onToggleDirection}
					aria-label={sortDirection === "asc" ? "Absteigend sortieren" : "Aufsteigend sortieren"}
					data-testid="agenda-sort-direction"
				>
					<SortIcon size={14} />
				</button>
				<PopoverPrimitive.Trigger asChild>
					<button
						className="inline-flex items-center h-full px-2 hover:bg-accent rounded-r-md"
						aria-label="Sortierfeld wählen"
						data-testid="agenda-sort-field"
					>
						{labels[sortField]}
					</button>
				</PopoverPrimitive.Trigger>
			</div>
			<PopoverPrimitive.Portal>
				<PopoverPrimitive.Content
					className="z-50 w-[180px] rounded-md border bg-popover text-popover-foreground shadow-md p-1"
					align="end"
					sideOffset={4}
				>
					{(Object.keys(labels) as SortField[]).map((field) => (
						<button
							key={field}
							className={clsx(
								"flex w-full items-center rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent gap-2",
								sortField === field && "bg-accent"
							)}
							onClick={() => {
								onFieldChange(field);
								setOpen(false);
							}}
						>
							{sortField === field && <Check className="h-3 w-3" />}
							{sortField !== field && <span className="w-3" />}
							{labels[field]}
						</button>
					))}
				</PopoverPrimitive.Content>
			</PopoverPrimitive.Portal>
		</PopoverPrimitive.Root>
	);
};

const AgendaFilterButton: React.FC<{
	statusFilter: number[];
	onStatusFilterChange: (statuses: number[]) => void;
	leavingSoonEnabled: boolean;
	leavingSoonDays: number;
	onLeavingSoonEnabledChange: (enabled: boolean) => void;
	onLeavingSoonDaysChange: (days: number) => void;
	activeCount: number;
	leavingSoonLabel?: string;
}> = ({
	statusFilter,
	onStatusFilterChange,
	leavingSoonEnabled,
	leavingSoonDays,
	onLeavingSoonEnabledChange,
	onLeavingSoonDaysChange,
	activeCount,
	leavingSoonLabel = "Ausgang in",
}) => {
	const [open, setOpen] = useState(false);

	const handleStatusToggle = (status: number) => {
		if (statusFilter.includes(status)) {
			onStatusFilterChange(statusFilter.filter((s) => s !== status));
		} else {
			onStatusFilterChange([...statusFilter, status]);
		}
	};

	return (
		<PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
			<PopoverPrimitive.Trigger asChild>
				<button
					className="inline-flex items-center justify-center h-8 rounded-md px-3 text-xs gap-1.5 border border-input bg-background hover:bg-accent hover:text-accent-foreground"
					aria-label="Filter"
					data-testid="agenda-filter-button"
				>
					<Filter size={14} />
					Filter{activeCount > 0 && ` (${activeCount})`}
				</button>
			</PopoverPrimitive.Trigger>
			<PopoverPrimitive.Portal>
				<PopoverPrimitive.Content
					className="z-50 w-[260px] rounded-md border bg-popover text-popover-foreground shadow-md"
					align="end"
					sideOffset={4}
				>
					<div className="p-2 border-b">
						<div className="text-xs font-medium text-muted-foreground mb-1.5">Status</div>
						<div className="flex flex-col gap-0.5">
							{FILTER_STATUSES.map((status) => {
								const selected = statusFilter.includes(status);
								return (
									<div
										key={status}
										onClick={() => handleStatusToggle(status)}
										className="flex items-center gap-2 px-2 py-1 rounded-sm cursor-pointer hover:bg-accent text-sm"
									>
										<CheckboxPrimitive.Root
											checked={selected}
											className="h-3.5 w-3.5 shrink-0 rounded-sm border border-primary pointer-events-none data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
										>
											<CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
												<Check className="h-3 w-3" />
											</CheckboxPrimitive.Indicator>
										</CheckboxPrimitive.Root>
										<span
											className={clsx(
												"h-3 w-3 rounded-[3px] shrink-0",
												getJobColorClass(status, false)
											)}
											aria-hidden
										/>
										<span>{jobStatusToLabel[status] ?? `Status ${status}`}</span>
									</div>
								);
							})}
						</div>
						<div className="mt-1 flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground">
							<span className="h-3.5 w-3.5 shrink-0" aria-hidden />
							<span
								className={clsx(
									"h-3 w-3 rounded-[3px] shrink-0 ring-1 ring-inset ring-white/70",
									PENDENT_UNTOUCHED_COLOR
								)}
								aria-hidden
							/>
							<span>Pendent · unbearbeitet</span>
						</div>
					</div>
					<div className="p-2">
						<div className="flex items-center gap-2">
							<CheckboxPrimitive.Root
								checked={leavingSoonEnabled}
								onCheckedChange={(checked) => onLeavingSoonEnabledChange(!!checked)}
								className="h-3.5 w-3.5 shrink-0 rounded-sm border border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
								data-testid="agenda-leaving-soon-toggle"
							>
								<CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
									<Check className="h-3 w-3" />
								</CheckboxPrimitive.Indicator>
							</CheckboxPrimitive.Root>
							<span className="text-sm">{leavingSoonLabel}</span>
							<input
								type="number"
								min={1}
								max={30}
								value={leavingSoonDays}
								onChange={(e) => onLeavingSoonDaysChange(Math.max(1, parseInt(e.target.value) || 1))}
								className="w-12 h-6 text-xs text-center border rounded-sm"
								data-testid="agenda-leaving-soon-days"
							/>
							<span className="text-sm">Tagen</span>
						</div>
					</div>
					{(statusFilter.length > 0 || leavingSoonEnabled) && (
						<div className="flex border-t p-1.5">
							<button
								className="flex-1 h-7 text-xs rounded-md hover:bg-accent hover:text-accent-foreground"
								onClick={() => {
									onStatusFilterChange([]);
									onLeavingSoonEnabledChange(false);
								}}
							>
								Filter zurücksetzen
							</button>
						</div>
					)}
				</PopoverPrimitive.Content>
			</PopoverPrimitive.Portal>
		</PopoverPrimitive.Root>
	);
};

const GroupingButton: React.FC<{
	groupMode: GroupMode;
	onGroupModeChange: (mode: GroupMode) => void;
	groupLabel?: string;
}> = ({ groupMode, onGroupModeChange, groupLabel = "Kunden" }) => {
	const [open, setOpen] = useState(false);
	const singular = groupLabel.slice(0, -1);
	const options: { value: GroupMode; label: string }[] = [
		{ value: "flat", label: "Nicht gruppiert" },
		{ value: "client", label: `Nach ${singular}` },
	];

	return (
		<PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
			<PopoverPrimitive.Trigger asChild>
				<button
					className="inline-flex items-center justify-center h-8 rounded-md px-3 text-xs gap-1.5 border border-input bg-background hover:bg-accent hover:text-accent-foreground"
					aria-label="Gruppierung"
					data-testid="agenda-grouping-button"
				>
					<Layers size={14} />
					{groupMode === "flat" ? "Nicht gruppiert" : singular}
				</button>
			</PopoverPrimitive.Trigger>
			<PopoverPrimitive.Portal>
				<PopoverPrimitive.Content
					className="z-50 w-[160px] rounded-md border bg-popover text-popover-foreground shadow-md p-1"
					align="end"
					sideOffset={4}
				>
					{options.map((opt) => (
						<button
							key={opt.value}
							className={clsx(
								"flex w-full items-center rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent gap-2",
								groupMode === opt.value && "bg-accent"
							)}
							onClick={() => {
								onGroupModeChange(opt.value);
								setOpen(false);
							}}
						>
							{groupMode === opt.value && <Check className="h-3 w-3" />}
							{groupMode !== opt.value && <span className="w-3" />}
							{opt.label}
						</button>
					))}
				</PopoverPrimitive.Content>
			</PopoverPrimitive.Portal>
		</PopoverPrimitive.Root>
	);
};

const ROW_DENSITY_STEPS = [
	{ label: "XS", value: 16 },
	{ label: "S", value: 20 },
	{ label: "M", value: 24 },
	{ label: "L", value: 30 },
	{ label: "XL", value: 36 },
];

const RowDensityButton: React.FC<{
	rowHeight: number;
	onRowHeightChange: (height: number) => void;
}> = ({ rowHeight, onRowHeightChange }) => {
	const currentIdx = ROW_DENSITY_STEPS.findIndex((s) => s.value === rowHeight);
	const currentLabel = currentIdx >= 0 ? ROW_DENSITY_STEPS[currentIdx].label : "S";

	return (
		<PopoverPrimitive.Root>
			<PopoverPrimitive.Trigger asChild>
				<button
					className="inline-flex items-center justify-center h-8 rounded-md px-3 text-xs gap-1.5 border border-input bg-background hover:bg-accent hover:text-accent-foreground"
					aria-label="Zeilendichte"
					data-testid="agenda-row-density-button"
				>
					<Rows3 size={14} />
					{currentLabel}
				</button>
			</PopoverPrimitive.Trigger>
			<PopoverPrimitive.Portal>
				<PopoverPrimitive.Content
					className="z-50 w-[120px] rounded-md border bg-popover text-popover-foreground shadow-md p-1"
					align="end"
					sideOffset={4}
				>
					{ROW_DENSITY_STEPS.map((step) => (
						<button
							key={step.value}
							className={clsx(
								"flex w-full items-center rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent gap-2",
								rowHeight === step.value && "bg-accent"
							)}
							onClick={() => onRowHeightChange(step.value)}
						>
							{rowHeight === step.value && <Check className="h-3 w-3" />}
							{rowHeight !== step.value && <span className="w-3" />}
							{step.label}
						</button>
					))}
				</PopoverPrimitive.Content>
			</PopoverPrimitive.Portal>
		</PopoverPrimitive.Root>
	);
};

const PollingIndicator: React.FC<{ lastPolledAt: Date | null }> = ({ lastPolledAt }) => {
	const [, setTick] = useState(0);

	useEffect(() => {
		if (!lastPolledAt) return;
		const interval = setInterval(() => setTick((t) => t + 1), 10_000);
		return () => clearInterval(interval);
	}, [lastPolledAt]);

	if (!lastPolledAt) return null;

	const seconds = Math.floor((Date.now() - lastPolledAt.getTime()) / 1000);
	const label =
		seconds < 10
			? "gerade eben"
			: seconds < 60
				? `vor ${seconds}s`
				: `vor ${Math.floor(seconds / 60)}m`;

	return (
		<span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground tabular-nums" aria-label="Letzte Aktualisierung" data-testid="agenda-polling-indicator">
			<span className="relative flex h-2 w-2">
				<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
				<span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
			</span>
			{label}
		</span>
	);
};

interface GroupFilterProps {
	groups: AgendaGroup[];
	filteredGroupIds: string[];
	onFilterChange: (ids: string[]) => void;
	groupLabel?: string;
}

const GroupFilter: React.FC<GroupFilterProps> = ({
	groups,
	filteredGroupIds,
	onFilterChange,
	groupLabel = "Kunden",
}) => {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");

	const sortedGroups = useMemo(
		() => [...groups].sort((a, b) => a.name.localeCompare(b.name, "de")),
		[groups]
	);

	const filtered = useMemo(
		() =>
			search
				? sortedGroups.filter((g) =>
						g.name.toLowerCase().includes(search.toLowerCase())
					)
				: sortedGroups,
		[sortedGroups, search]
	);

	const handleToggle = useCallback(
		(groupId: string) => {
			if (filteredGroupIds.length === 0) {
				onFilterChange(
					groups
						.filter((g) => g.id !== groupId)
						.map((g) => g.id)
				);
			} else if (filteredGroupIds.includes(groupId)) {
				const next = filteredGroupIds.filter((id) => id !== groupId);
				onFilterChange(next.length === groups.length ? [] : next);
			} else {
				const next = [...filteredGroupIds, groupId];
				onFilterChange(next.length === groups.length ? [] : next);
			}
		},
		[filteredGroupIds, groups, onFilterChange]
	);

	const label =
		filteredGroupIds.length === 0
			? `${groupLabel} wählen...`
			: `${filteredGroupIds.length} ${groupLabel}`;

	return (
		<PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
			<PopoverPrimitive.Trigger asChild>
				<button
					className="inline-flex items-center justify-center h-8 rounded-md px-3 text-xs gap-1.5 border border-input bg-background hover:bg-accent hover:text-accent-foreground"
					aria-label={`${groupLabel} filtern`}
					data-testid="agenda-client-filter"
				>
					<Filter size={14} />
					{label}
				</button>
			</PopoverPrimitive.Trigger>
			<PopoverPrimitive.Portal>
				<PopoverPrimitive.Content
					className="z-50 w-[260px] rounded-md border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
					align="end"
					sideOffset={4}
				>
					<div className="flex items-center border-b px-3">
						<input
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder={`${groupLabel.slice(0, -1)} suchen...`}
							className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
						/>
					</div>
					<div className="max-h-[300px] overflow-y-auto p-1">
						{filtered.length === 0 && (
							<div className="py-6 text-center text-sm text-muted-foreground">
								Kein Ergebnis gefunden.
							</div>
						)}
						{filtered.map((group) => {
							const selected =
								filteredGroupIds.length === 0 ||
								filteredGroupIds.includes(group.id);
							return (
								<div
									key={group.id}
									onClick={() => handleToggle(group.id)}
									className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground gap-2"
								>
									<CheckboxPrimitive.Root
										checked={selected}
										className="peer h-4 w-4 shrink-0 rounded-sm border border-primary pointer-events-none data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
									>
										<CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
											<Check className="h-4 w-4" />
										</CheckboxPrimitive.Indicator>
									</CheckboxPrimitive.Root>
									<span className="truncate text-sm">
										{group.name}
									</span>
								</div>
							);
						})}
					</div>
					{filteredGroupIds.length > 0 && (
						<div className="flex border-t p-1.5">
							<button
								className="flex-1 h-7 text-xs rounded-md hover:bg-accent hover:text-accent-foreground"
								onClick={() => onFilterChange([])}
							>
								Filter zurücksetzen
							</button>
						</div>
					)}
				</PopoverPrimitive.Content>
			</PopoverPrimitive.Portal>
		</PopoverPrimitive.Root>
	);
};
